/**
 * RPC client for the Fade worker.
 *
 * We deliberately bypass Hono's typed `hc()` because the cross-package
 * `import type` chain in this monorepo is brittle (Bun + TS bundler
 * resolution loses the type through `import type` indirection). Instead,
 * we hand-write thin typed wrappers over `fetch()`.
 *
 * The contract is mirrored from `apps/worker/src/app-type.ts`. If you
 * add or rename a route, update both.
 */

import type {
  CommentRow,
  CreateCommentInput,
  CreateLegacyInput,
  DmsState,
  FeedItem,
  FeedPage,
  LegacyRow,
  SetLegacyStatusInput,
  UpdateDmsInput,
  UpdateLegacyInput,
} from './types';

async function call<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (init.json !== undefined) {
    init.body = JSON.stringify(init.json);
    delete (init as { json?: unknown }).json;
  }
  const res = await fetch(path, { ...init, headers, credentials: 'include' });
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Feed
  feed: {
    list: (page = 1, pageSize = 20) =>
      call<FeedPage>(`/api/feed?page=${page}&pageSize=${pageSize}`),
  },

  // Random story
  random: {
    get: async (): Promise<FeedItem | null> => {
      const res = await fetch('/api/random', { credentials: 'include' });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return (await res.json()) as FeedItem;
    },
  },

  // Author's own legacies
  myLegacies: {
    list: () => call<{ items: LegacyRow[] }>('/api/me/legacies'),
    create: (input: CreateLegacyInput) =>
      call<LegacyRow>('/api/me/legacies', { method: 'POST', json: input }),
    get: (id: string) => call<LegacyRow>(`/api/me/legacies/${id}`),
    update: (id: string, input: UpdateLegacyInput) =>
      call<LegacyRow>(`/api/me/legacies/${id}`, { method: 'PATCH', json: input }),
    setStatus: (id: string, input: SetLegacyStatusInput) =>
      call<LegacyRow>(`/api/me/legacies/${id}/status`, { method: 'PUT', json: input }),
    delete: async (id: string) => {
      await call<void>(`/api/me/legacies/${id}`, { method: 'DELETE' });
    },
  },

  // Comments
  comments: {
    list: (legacyId: string) =>
      call<{ items: CommentRow[] }>(`/api/legacies/${legacyId}/comments`),
    create: (legacyId: string, input: CreateCommentInput) =>
      call<CommentRow>(`/api/legacies/${legacyId}/comments`, { method: 'POST', json: input }),
    delete: async (legacyId: string, commentId: string) => {
      await call<void>(`/api/legacies/${legacyId}/comments/${commentId}`, { method: 'DELETE' });
    },
  },

  // Remember ❤️
  remember: {
    get: async (legacyId: string): Promise<{ remembered: boolean }> => {
      const res = await fetch(`/api/legacies/${legacyId}/remember`, { credentials: 'include' });
      if (res.status === 401) return { remembered: false };
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return (await res.json()) as { remembered: boolean };
    },
    remember: (legacyId: string) =>
      call<{ remembered: boolean; changed: boolean }>(
        `/api/legacies/${legacyId}/remember`,
        { method: 'POST' },
      ),
    forget: (legacyId: string) =>
      call<{ remembered: boolean; changed: boolean }>(
        `/api/legacies/${legacyId}/remember`,
        { method: 'DELETE' },
      ),
  },

  // DMS
  dms: {
    get: () => call<DmsState>('/api/dms/me'),
    update: (input: UpdateDmsInput) =>
      call<DmsState>('/api/dms/me', { method: 'PUT', json: input }),
    checkIn: () => call<DmsState>('/api/dms/me/check-in', { method: 'POST' }),
  },
};